import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../store/auth'

// Lazy-loaded components
const Login = () => import('../views/Login.vue')
const Dashboard = () => import('../views/Dashboard.vue')
const RequestList = () => import('../views/RequestList.vue')
const RequestForm = () => import('../views/RequestForm.vue')
const RequestDetail = () => import('../views/RequestDetail.vue')
const ApprovalList = () => import('../views/ApprovalList.vue')
const ProcessedRequestList = () => import('../views/ProcessedRequestList.vue')
const ExpensesSummary = () => import('../views/ExpensesSummary.vue')
const NotificationList = () => import('../views/NotificationList.vue')
const UserList = () => import('../views/UserList.vue')
const UserForm = () => import('../views/UserForm.vue')
const UserProfile = () => import('../views/UserProfile.vue')
const ResetPassword = () => import('../views/ResetPassword.vue')
const NotFound = () => import('../views/NotFound.vue')

const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'UserProfile',
    component: UserProfile,
    meta: { requiresAuth: true }
  },
  {
    path: '/requests',
    name: 'RequestList',
    component: RequestList,
    meta: { requiresAuth: true }
  },
  {
    path: '/requests/new',
    name: 'RequestCreate',
    component: RequestForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/requests/:id',
    name: 'RequestDetail',
    component: RequestDetail,
    meta: { requiresAuth: true },
    props: true
  },
  {
    path: '/requests/:id/edit',
    name: 'RequestEdit',
    component: RequestForm,
    meta: { requiresAuth: true },
    props: true
  },
  {
    path: '/approvals',
    name: 'ApprovalList',
    component: ApprovalList,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/processed-requests',
    name: 'ProcessedRequestList',
    component: ProcessedRequestList,
    meta: { requiresAuth: true }
  },
  {
    path: '/expenses-summary',
    name: 'ExpensesSummary',
    component: ExpensesSummary,
    meta: { requiresAuth: true, requiresManager: true }
  },
  {
    path: '/notifications',
    name: 'NotificationList',
    component: NotificationList,
    meta: { requiresAuth: true }
  },
  {
    path: '/users',
    name: 'UserList',
    component: UserList,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/users/new',
    name: 'UserCreate',
    component: UserForm,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/users/:id',
    name: 'UserEdit',
    component: UserForm,
    meta: { requiresAuth: true, requiresAdmin: true },
    props: true
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)
  const requiresManager = to.matched.some(record => record.meta.requiresManager)
  const userRole = authStore.user?.role
  
  // Si la route nécessite une authentification et que l'utilisateur n'est pas connecté
  if (requiresAuth && !authStore.isAuthenticated) {
    next('/login')
  } 
  // Si la route nécessite des droits admin et que l'utilisateur n'est pas admin
  else if (requiresAdmin && userRole !== 'admin') {
    next('/dashboard')
  } 
  // Si la route nécessite des droits manager et que l'utilisateur n'est ni manager ni admin
  else if (requiresManager && userRole !== 'manager' && userRole !== 'admin') {
    next('/dashboard')
  }
  // Si l'utilisateur est déjà connecté et essaie d'accéder à la page de connexion
  else if (to.path === '/login' && authStore.isAuthenticated) {
    next('/dashboard')
  } 
  // Dans tous les autres cas, continuer normalement
  else {
    next()
  }
})

export default router
